import { PropsWithChildren } from 'react';
import { InformationmarkSmallCircleIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { Spacing } from '@zep/ui';
import { fDateTimeByCountryCode } from '../../../../utils';
import { useExpectedPlanPrice, usePlanStatusType } from '../hooks';
import { usePlanPaymentFormContext, usePlanPaymentInfoContext } from '../PlanPaymentSection/Providers';
import { PlanTemplateType } from '../types';
export const PlanNotifications = () => {
  const {
    selectedPlanInfo,
    isUpdate,
    spaceHashId
  } = usePlanPaymentInfoContext();
  const {
    watch
  } = usePlanPaymentFormContext();
  const planType = watch('planType');
  const planTemplateType = watch('planTemplateType');
  const planStatusType = usePlanStatusType({
    planType
  });
  const {
    추가결제예상금액
  } = useExpectedPlanPrice({
    selectedPlanInfo,
    enabled: isUpdate && planTemplateType === PlanTemplateType.PRODUCT_SUBSCRIPTION,
    spaceHashId
  });
  const isOnetimePlan = planTemplateType === PlanTemplateType.PRODUCT_ONETIME;
  return <>
      {planTemplateType === PlanTemplateType.PRODUCT_ONETIME && <TaxBillingAlert />}
      {isUpdate && planStatusType === 'SAME_GRADE' && <IsSamePlan />}
      {isUpdate && planStatusType === 'UP_GRADE' && 추가결제예상금액 <= 0 && <IsUpgradeButCheaperPlan />}
      {isUpdate && planStatusType === 'DOWN_GRADE' && <IsDowngradePlan />}
      {isOnetimePlan && !(planStatusType === 'SAME_GRADE' && isUpdate) && <IsOnetimePlan />}
      {isUpdate && planStatusType === 'UP_GRADE' && <IsUpgradePlan />}
    </>;
};
const TaxBillingAlert = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const taxInvoiceLink = t('subscription:payment.taxInvoice.anchorTextLink');
  return <PlanNotification data-sentry-element="PlanNotification" data-sentry-component="TaxBillingAlert" data-sentry-source-file="PlanNotifications.tsx">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3xs">
          <InformationmarkSmallCircleIcon width={13} height={13} className="text-gray-900/80" data-sentry-element="InformationmarkSmallCircleIcon" data-sentry-source-file="PlanNotifications.tsx" />
          <span className="text-caption-1 font-semibold text-gray-600">
            {t('subscription:payment.summary.alert7', '세금계산서 발행이 필요하신가요?')}
          </span>
        </div>
        <a href={taxInvoiceLink} target="_blank">
          <span className="text-caption-1 font-semibold text-blue underline">
            {t('subscription:payment.taxInvoice.anchorText', '신청서 작성')}
          </span>
        </a>
      </div>
    </PlanNotification>;
};
const IsSamePlan = () => {
  const {
    t
  } = useTranslation(['subscription']);
  return <PlanNotification data-sentry-element="PlanNotification" data-sentry-component="IsSamePlan" data-sentry-source-file="PlanNotifications.tsx">
      <div className="flex items-center gap-3xs">
        <InformationmarkSmallCircleIcon width={13} height={13} className="text-gray-900/80" data-sentry-element="InformationmarkSmallCircleIcon" data-sentry-source-file="PlanNotifications.tsx" />
        <span className="text-caption-1 font-semibold text-gray-600">
          {t('subscription:payment.summary.alert2', '현재 이용 중인 플랜입니다')}
        </span>
      </div>
    </PlanNotification>;
};
const IsUpgradeButCheaperPlan = () => {
  const {
    t
  } = useTranslation(['subscription']);
  return <PlanNotification data-sentry-element="PlanNotification" data-sentry-component="IsUpgradeButCheaperPlan" data-sentry-source-file="PlanNotifications.tsx">
      <div className="flex w-full flex-col">
        <div className="flex items-center gap-3xs">
          <InformationmarkSmallCircleIcon width={13} height={13} className="text-gray-900/80" data-sentry-element="InformationmarkSmallCircleIcon" data-sentry-source-file="PlanNotifications.tsx" />
          <span className="text-caption-1 font-semibold text-gray-600">
            {t('subscription:payment.summary.alert5', '이용 중이던 플랜의 결제된 금액은 환불되지 않습니다')}
          </span>
        </div>
        <span className="text-caption-1 font-semibold text-gray-600">
          {t('subscription:payment.summary.alert5Description', '다음 결제일 전에 다시 플랜을 변경할 경우 미사용 금액 차감에 불이익이 있을 수 있습니다.')}
        </span>
      </div>
    </PlanNotification>;
};
const IsUpgradePlan = () => {
  const {
    t
  } = useTranslation(['subscription']);
  return <PlanNotification data-sentry-element="PlanNotification" data-sentry-component="IsUpgradePlan" data-sentry-source-file="PlanNotifications.tsx">
      <div className="flex items-center gap-3xs">
        <InformationmarkSmallCircleIcon width={13} height={13} className="text-gray-900/80" data-sentry-element="InformationmarkSmallCircleIcon" data-sentry-source-file="PlanNotifications.tsx" />
        <span className="text-caption-1 font-semibold text-gray-600">
          {t('subscription:payment.summary.alert6', '결제 이후 새로운 플랜이 바로 적용됩니다')}
        </span>
      </div>
    </PlanNotification>;
};
const IsDowngradePlan = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    planPaymentInfo
  } = usePlanPaymentInfoContext();
  return <PlanNotification data-sentry-element="PlanNotification" data-sentry-component="IsDowngradePlan" data-sentry-source-file="PlanNotifications.tsx">
      <div className="flex w-full flex-col">
        <div className="mb-1 flex items-center gap-3xs">
          <InformationmarkSmallCircleIcon width={13} height={13} className="text-gray-900/80" data-sentry-element="InformationmarkSmallCircleIcon" data-sentry-source-file="PlanNotifications.tsx" />
          <span className="text-caption-1 font-semibold text-gray-600">
            {t('subscription:payment.summary.alert4', '구매한 플랜은 현재 플랜 종료 후 적용됩니다')}
          </span>
        </div>
        <span className="text-caption-1 font-medium text-gray-600">
          {t('subscription:payment.summary.alert4Description', {
          defaultValues: '현재 이용 중인 {{plan}} {{ccu}}플랜은 {{date}}까지 사용 가능합니다.',
          plan: planPaymentInfo?.subscription?.planType,
          ccu: planPaymentInfo?.subscription?.maxCcu,
          date: fDateTimeByCountryCode(planPaymentInfo?.spacePlan?.endDate ?? '', planPaymentInfo?.space.countryCode ?? 'KR')
        })}
        </span>
      </div>
    </PlanNotification>;
};

// TODO: 다른 카드도 바꿔야 함
const IsOnetimePlan = () => {
  const {
    t
  } = useTranslation(['subscription']);
  return <PlanNotification data-sentry-element="PlanNotification" data-sentry-component="IsOnetimePlan" data-sentry-source-file="PlanNotifications.tsx">
      <div className="flex items-center gap-3xs">
        <span>
          <InformationmarkSmallCircleIcon width={13} height={13} className="text-gray-900/80" data-sentry-element="InformationmarkSmallCircleIcon" data-sentry-source-file="PlanNotifications.tsx" />
        </span>
        <span className="text-caption-1 font-semibold text-gray-600">
          {t('subscription:payment.summary.alert1', '결제 후 인원 수와 플랜을 낮출 수 없습니다')}
        </span>
      </div>
    </PlanNotification>;
};
const PlanNotification = (props: PropsWithChildren) => {
  return <>
      <div className='className="w-full rounded-xs border border-[#27262E14] bg-[#27262E0A] px-[20px] py-[10px]'>
        {props.children}
      </div>
      <Spacing size={10} data-sentry-element="Spacing" data-sentry-source-file="PlanNotifications.tsx" />
    </>;
};